import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import {
  CandidateloginApi,
  CompanyShortNameApi,
  sessionLogoutApi,
  ValidateOtp,
} from "../Utils/Axios";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../Context/AuthContext";
import "../LayOut/NewLogin/Message.css";
import "./TenantBranding.css";
import Loader from "../Utils/Loader";
import { setAuthDetails } from "../Redux/AuthSlice";
import { useDispatch } from "react-redux";

const CandidateLogin = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      username: "",
      password: "",
      otp: "",
    },
  });

  const { setAuthDetails: setContextAuthDetails } = useAuth();
  const { company } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [branding, setBranding] = useState(location.state?.branding || null);
  const [brandingLoading, setBrandingLoading] = useState(!location.state?.branding);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [otpTimeLimit, setOtpTimeLimit] = useState(180);
  const [expiryTime, setExpiryTime] = useState(null);
  const [otpExpired, setOtpExpired] = useState(false);
  const [showSessionButton, setShowSessionButton] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    localStorage.setItem("companyName", company);
  }, [company]);

  useEffect(() => {
    if (branding) return;
    CompanyShortNameApi(company)
      .then((res) => setBranding(res.data.data))
      .catch(() => {})
      .finally(() => setBrandingLoading(false));
  }, []);

  useEffect(() => {
    if (!otpSent || !expiryTime) return;

    const timer = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.floor((expiryTime - Date.now()) / 1000)
      );
      setOtpTimeLimit(remaining);

      if (remaining <= 0) {
        clearInterval(timer);
        setOtpExpired(true);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [otpSent, expiryTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const sendOtp = (data) => {
    const payload = {
      username: data.username,
      company: company,
    };

    setLoading(true);
    CandidateloginApi(payload)
      .then((response) => {
        const token = response.data?.token;
        if (token) {
          localStorage.setItem("token", token);
          const decodedToken = jwtDecode(token);
          const {
            sub: userId,
            roles: userRole,
            company,
            employee: employeeId,
            resourceType,
          } = decodedToken;

          dispatch(
            setAuthDetails({
              userId,
              userRole,
              company,
              employeeId,
              resourceType,
              source: "company",
            })
          );
          setContextAuthDetails(token);
          toast.success("OTP Sent Successfully");
          setOtpSent(true);
          setOtpExpired(false);
          setOtpTimeLimit(180); // Reset OTP time limit to 3 minutes
          setExpiryTime(Date.now() + 180 * 1000)
          setShowOtpField(true); // Show OTP field
        } else {
          console.error("Token not found in response");
          setErrorMessage("Unexpected response format. Token not found.");
          setShowErrorModal(true);
          setOtpSent(false);
          reset("");
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        handleLoginError(error, payload)
      });
  };

  const resendOtp = () => {
    const currentValues = getValues();
    reset({ username: currentValues.username });
    setOtpExpired(false);
    setOtpTimeLimit(180);
    setExpiryTime(Date.now() + 180 * 1000); // Reset expiry to 3 minutes from now
    sendOtp(currentValues);
  };

  const verifyOtpAndCompanyLogin = (data) => {
    const payload = {
      username: data.username,
      otp: data.otp,
      company: company,
    };
    setLoading(true);
    ValidateOtp(payload)
      .then((response) => {
        setLoading(false);
        toast.success("Login Successful", {
          position: "top-right",
          transition: Bounce,
          hideProgressBar: true,
          theme: "colored",
          autoClose: 2000,
        });
        setTimeout(() => {
          navigate("/documentUpload");
        }, 1000);
      })
      .catch((error) => {
        setLoading(false);
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          const errorMessage = error.response.data.error.message;
          setErrorMessage(errorMessage);
          setShowErrorModal(true);
        } else {
          setErrorMessage("Login failed. Please try again later.");
          setShowErrorModal(true);
        }
        if (otpTimeLimit <= 0) {
          setOtpExpired(true);
          setErrorMessage("OTP Expired. Please Resend OTP");
          setShowErrorModal(true);
        }
      });
  };

  const handleSessionTimeout = async () => {
    if (!sessionData) return;
    setLoading(true);
    console.log("sessionData", sessionData);
    try {
      await sessionLogoutApi(sessionData); // pass the stored login data
      toast.success("Previous session logged out successfully.");
      setShowErrorModal(false);
      setShowSessionButton(false);
      setSessionData(null);
      reset(); // reset form fields
      navigate(`/${company}/candidateLogin`); // redirect to login page
    } catch (err) {
      toast.error("Failed to logout previous session.");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginError = (error, loginData) => {
    let errorMessage = "Login failed. Please try again later.";
    let showSessionButton = false;

    if (error.response && error.response.data) {
      const data = error.response.data;

      if (data.isUserLoggedIn) {
        errorMessage = (
          <>
            {data.message || "User is already logged in."}
            <br />
            You need to log out from the old session to continue.
          </>
        );
        showSessionButton = true;
        setSessionData(loginData); // store login data to call sessionLogoutApi
      } else if (data.error && data.error.message) {
        errorMessage = data.error.message;
      }
    }

    setErrorMessage(errorMessage);
    setShowSessionButton(showSessionButton);
    setShowErrorModal(true);
  };

  const closeModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
  };

  const handleEmailChange = (e) => {
    if (e.keyCode === 32) {
      e.preventDefault();
    }
  };

  const onSubmit = (data) => {
    if (otpSent && !otpExpired) {
      verifyOtpAndCompanyLogin(data);
    } else {
      sendOtp(data);
    }
  };

  return (
    <div>
      <main className="newLoginMainWrapper">
        {loading && <Loader />}
        <div className="newLoginWrapper">
          <div className="newLoginContainer">
            <div className="newLoginLeftSectionOuter">
              {brandingLoading ? (
                <div className="tenant-skeleton">
                  <div className="tenant-skeleton-avatar" />
                  <div className="tenant-skeleton-line" style={{ width: 160 }} />
                  <div className="tenant-skeleton-line" style={{ width: 110 }} />
                </div>
              ) : branding ? (
                <div className="tenant-branding-panel">
                  <div className="tenant-welcome-text">Welcome to CUB-Hrm</div>
                  {branding.imageFile ? (
                    <div className="tenant-logo-wrapper">
                      <img
                        src={branding.imageFile}
                        alt={branding.companyName}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </div>
                  ) : (
                    <div className="tenant-initials-avatar">
                      {(branding.companyName || company)
                        .split(" ")
                        .slice(0, 2)
                        .map((w) => w[0])
                        .join("")
                        .toUpperCase()}
                    </div>
                  )}
                  <div className="tenant-company-name">
                    {branding.companyName}
                  </div>
                  <div className="tenant-tagline">Welcome to your workspace</div>
                  <div className="tenant-meta">
                    {branding.industry && (
                      <span className="tenant-industry-badge">{branding.industry}</span>
                    )}
                    {branding.websiteUrl && (
                      <a
                        href={branding.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tenant-website-link"
                      >
                        <i className="bi bi-box-arrow-up-right" style={{ fontSize: 11 }} />
                        {branding.websiteUrl.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="newLoginLeftTitle">
                    Welcome To <br />
                  </div>
                  <div className="newLoginLeftImgHolder">
                    <img src="..\assets\img\left-img.png" alt="#" />
                  </div>
                </>
              )}
            </div>
            <div className="newLoginRightSectionOuter">
              <div className="newLoginRightSection">
                {branding && (
                  <div className="tenant-mobile-header">
                    {branding.imageFile ? (
                      <div className="tenant-mobile-logo">
                        <img
                          src={branding.imageFile}
                          alt={branding.companyName}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      </div>
                    ) : (
                      <div className="tenant-mobile-initials">
                        {(branding.companyName || company)
                          .split(" ")
                          .slice(0, 2)
                          .map((w) => w[0])
                          .join("")
                          .toUpperCase()}
                      </div>
                    )}
                    <div className="tenant-mobile-name">
                      {branding.legalCompanyName || branding.companyName}
                    </div>
                  </div>
                )}
                <div className="newLoginRightSecTitle">Login</div>
                <div className="newLoginRightSecSelectLogin">
                  <div className="loginBtn">
                    <span>Continue With Company Login</span>
                  </div>
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="formgroup">
                    <label className="form-label">Email Id</label>
                    <input
                      className="form-control form-control-lg"
                      type="email"
                      name="email"
                      placeholder="Email Id"
                      autoComplete="off"
                      onKeyDown={handleEmailChange}
                      readOnly={otpSent && !otpExpired}
                      {...register("username", {
                        required: "Email Id is Required.",
                        pattern: {
                          value:
                            /^[a-z][a-zA-Z0-9._+-]*@[a-zA-Z0-9.-]+\.(com|in|org|net|edu|gov)$/,
                          message: "Invalid Email Id Format",
                        },
                      })}
                    />
                    {errors.username && (
                      <p className="errorMsg" style={{ marginLeft: "20px" }}>
                        {errors.username.message}
                      </p>
                    )}
                  </div>

                  {otpSent && !otpExpired && (
                    <div className="formgroup">
                      <label className="form-label">OTP</label>
                      <input
                        className="form-control form-control-lg"
                        type="text"
                        name="otp"
                        id="otp"
                        placeholder="Enter Your OTP"
                        autoComplete="off"
                        maxLength={6}
                        inputMode="numeric"
                        {...register("otp", {
                          required: "OTP is Required.",
                          pattern: {
                            value: /^\d{6}$/,
                            message: "OTP must be 6 digits.",
                          },
                        })}
                      />
                      {errors.otp && (
                        <p className="errorMsg">{errors.otp.message}</p>
                      )}
                      <div className="otp-timer">
                        {otpTimeLimit > 0 ? (
                          <span className="text-primary">
                            OTP expires in: {formatTime(otpTimeLimit)}
                          </span>
                        ) : (
                          <span className="text-danger">OTP expired</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="d-grid gap-2 mt-3">
                    {otpExpired ? (
                      <button
                        className="btn btn-lg btn-primary"
                        type="button"
                        onClick={resendOtp}
                      >
                        Resend OTP
                      </button>
                    ) : (
                      <button className="btn btn-lg btn-primary" type="submit">
                        {otpSent ? "Verify OTP" : "Sign in"}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Modal
        show={showErrorModal}
        onHide={closeModal}
        centered
        style={{ zIndex: "1050" }}
        className="custom-modal"
      >
        <ModalHeader>
          <ModalTitle className="text-center">Error</ModalTitle>
          <button type="button" className="text-dark" aria-label="Close" onClick={closeModal}>
            X
          </button>
        </ModalHeader>
        <ModalBody className="text-center fs-bold">
          {errorMessage}
        </ModalBody>
        <ModalFooter>
          {showSessionButton && (
            <div className="mt-3">
              <button
                className="btn btn-secondary me-2"
                onClick={() => closeModal()}
              >
                No
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleSessionTimeout()}
              >
                Yes! Logout Previous Session
              </button>
            </div>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CandidateLogin;


